import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "util/cn";

const chipVariants = cva(
  "ui-chip inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "bg-eoe-bg-label dark:bg-eoe-dark-bg-label text-eoe-text dark:text-eoe-dark-text",
        primary: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
        secondary: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
        success: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
        warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
        error: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
        custom: "", // For custom colors via className
      },
      size: {
        sm: "px-1.5 py-0.5 text-xs",
        md: "px-3 py-1.5 text-xs",
        lg: "px-4 py-2 text-sm",
      },
      clickable: {
        true: "cursor-pointer hover:opacity-80",
        false: "",
      },
      deletable: {
        true: "pr-1.5",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      clickable: false,
      deletable: false,
    },
  }
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chipVariants> {
  label?: string;
  onDelete?: () => void;
  deleteIcon?: React.ReactNode;
  icon?: React.ReactNode;
  component?: React.ElementType;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  ({ 
    className, 
    variant, 
    size, 
    clickable, 
    deletable,
    label,
    children,
    onDelete,
    deleteIcon,
    icon,
    onClick,
    component: Component = "div",
    ...props 
  }, ref) => {
    const isClickable = clickable || !!onClick;
    const isDeletable = deletable || !!onDelete;

    return (
      <Component
        ref={ref}
        className={cn(
          chipVariants({ 
            variant, 
            size, 
            clickable: isClickable, 
            deletable: isDeletable,
            className 
          })
        )}
        onClick={onClick}
        {...props}
      >
        {icon && <span className="ui-chip-icon flex-shrink-0">{icon}</span>}
        {label || children}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="ui-chip-delete-button ml-1 flex-shrink-0 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
          >
            {deleteIcon || <X className="size-3" />}
          </button>
        )}
      </Component>
    );
  }
);
Chip.displayName = "Chip";

export { Chip, chipVariants };